/**
 * Otomatik kese ürünü sepete ekleme/çıkarma kontrolü.
 * api-bag-calculate section'ından gelen verilere göre kese ürününü yönetir.
 *
 * @class HPAutoBagControl
 */
class HPAutoBagControl {
  /** @type {string} */
  static SECTION_ID = 'api-bag-calculate';

  /** @type {string} */
  static DATA_ELEMENT_ID = 'api-bag-calculate-data';

  /** @type {string} */
  static EVENT_NAME = 'cart:bagCalculate';

  /**
   * api-bag-calculate section HTML'inden JSON verisini parse eder.
   *
   * @param {string} sectionHtml - response.sections['api-bag-calculate'] HTML string
   * @returns {Object|null} Parse edilmiş bag verisi veya null
   */
  static parseBagCalculateData(sectionHtml) {
    if (!sectionHtml || typeof sectionHtml !== 'string') return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const scriptEl = doc.getElementById(HPAutoBagControl.DATA_ELEMENT_ID);
      if (!scriptEl?.textContent) return null;
      return JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
      console.warn('hp-auto-bag-control: Failed to parse bag calculate data', e);
      return null;
    }
  }

  /**
   * Section Rendering API ile api-bag-calculate section'ını cart context'inde çeker.
   *
   * @returns {Promise<string|null>} Sayfa HTML (section içerir) veya null
   */
  static async fetchSectionHtml() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const url = `${cartUrl}?section_id=${HPAutoBagControl.SECTION_ID}`;
    try {
      const res = await fetch(url);
      return res.ok ? res.text() : null;
    } catch (e) {
      console.warn('hp-auto-bag-control: Failed to fetch section', e);
      return null;
    }
  }

  /**
   * Section HTML'den bag verisini alır. Önce response'tan, yoksa Section Rendering API ile.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   * @returns {Promise<Object|null>} Bag verisi veya null
   */
  static async getBagDataFromResponse(response) {
    let sectionHtml = response?.sections?.[HPAutoBagControl.SECTION_ID];

    if (!sectionHtml) {
      sectionHtml = await HPAutoBagControl.fetchSectionHtml();
    }

    return sectionHtml ? HPAutoBagControl.parseBagCalculateData(sectionHtml) : null;
  }

  /**
   * Cart API response'unda api-bag-calculate varsa event tetikler.
   * Yoksa Section Rendering API ile section çekip event tetikler.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   */
  static async dispatchBagCalculateIfNeeded(response) {
    const bagData = await HPAutoBagControl.getBagDataFromResponse(response);
    if (!bagData) return;

    document.dispatchEvent(
      new CustomEvent(HPAutoBagControl.EVENT_NAME, { detail: bagData })
    );
  }

  /**
   * Kese ürününü sepete ekler.
   *
   * @param {number} bagVariantId - Kese ürünü variant ID
   * @returns {Promise<void>}
   */
  static async addBagToCart(bagVariantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: bagVariantId, quantity: 1 }]
      }),
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * Sepetteki kese ürününün miktarını günceller (0 = sil, 1 = azalt).
   *
   * @param {number} bagLine - Kese ürününün sepetteki 1-based satır indexi
   * @param {number} quantity - Yeni miktar (0 veya 1)
   * @returns {Promise<void>}
   */
  static async changeBagQuantity(bagLine, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        line: bagLine,
        quantity,
        sections: HPAutoBagControl.SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (data?.sections) {
      HPAutoBagControl.dispatchBagCalculateIfNeeded(data);
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * cart:bagCalculate event'ini işler ve gerekli sepete ekleme/çıkarma işlemini yapar.
   *
   * @param {CustomEvent} event - cart:bagCalculate event
   */
  static handleBagCalculate(event) {
    const { bag_variant_id, should_add_bag, should_remove_bag, should_reduce_bag, bag_line } =
      event.detail;

    if (!bag_variant_id) return;

    if (should_add_bag) {
      HPAutoBagControl.addBagToCart(bag_variant_id).catch((err) =>
        console.error('hp-auto-bag-control: Failed to add bag', err)
      );
      return;
    }

    if ((should_remove_bag || should_reduce_bag) && bag_line) {
      const quantity = should_remove_bag ? 0 : 1;
      HPAutoBagControl.changeBagQuantity(bag_line, quantity).catch((err) =>
        console.error('hp-auto-bag-control: Failed to change bag quantity', err)
      );
    }
  }

  /**
   * Sayfa yüklendiğinde bir kez api-bag-calculate render edip kontrol sağlar.
   * Sepette fazla/eksik kese varsa düzeltir.
   */
  static async runInitialCheck() {
    const bagData = await HPAutoBagControl.getBagDataFromResponse({});
    if (!bagData) return;

    document.dispatchEvent(
      new CustomEvent(HPAutoBagControl.EVENT_NAME, { detail: bagData })
    );
  }

  /**
   * Event listener'ı kaydeder ve ilk kontrolü çalıştırır.
   */
  static init() {
    document.addEventListener(
      HPAutoBagControl.EVENT_NAME,
      HPAutoBagControl.handleBagCalculate
    );

    HPAutoBagControl.runInitialCheck().catch((err) =>
      console.warn('hp-auto-bag-control: Initial check failed', err)
    );
  }
}

// Global erişim için window'a bağla
window.dispatchBagCalculateIfNeeded = HPAutoBagControl.dispatchBagCalculateIfNeeded.bind(
  HPAutoBagControl
);

// Sayfa yüklendiğinde event listener'ı aktif et
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HPAutoBagControl.init());
} else {
  HPAutoBagControl.init();
}
